import { AxiosError } from "axios";
import clsx from "clsx";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { SubmitHandler, useForm } from "react-hook-form";

import {
  groupAccessIdFromGroupId,
  studentAccessIdFromUserId,
} from "../../../../../../collaboration/src/shared/access-id";
import { useAssessmentActions } from "../../../../utils/assessment";

import { getOrRunAiAssessment } from "components/ai/queries/assessment-cache";
import { AIAssessmentResponseType } from "components/ai/queries/assessment-query";
import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { useIsAssessing } from "components/materials/course/assessment/hooks";
import { OutcomeAssessment } from "components/materials/page/assessment/OutcomeAssessment";
import { calculatePointsFromOutcomeScores } from "components/materials/page/helpers";
import { PointsConfig } from "components/materials/page/outcomes/PointsConfig";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { LinkButton } from "mds/components/LinkButton";
import { showConfirmationModal } from "mds/components/Modal";
import { BaseTextArea } from "mds/components/TextArea";
import { DoubleSparkleIcon, LoadingDotsIcon } from "mds/icons";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectAssessmentBySubmissionId,
  selectCurrentCourseUserId,
  selectCurrentOrg,
  selectFullSubmissionById,
  selectIsAssessWithAIModeEnabled,
  selectNestedPageOutcomes,
  selectOutcomeAssessmentByAssessmentId,
  selectUserOfficialSubmissionByPageId,
} from "store/selectors";
import { isIndividualSubmission } from "store/selectors/type-helpers";
import { viewStateActions } from "store/slices/view";
import { toastErrorMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { useNavigate, useQueryParamsManager } from "utils/routing";

interface AssessmentGradingProps {
  page: PageType;
  nextURL?: URL;
  prevURL?: URL;
}

type AssessmentFormInputType = {
  points: number;
  comment: string;
  page_outcomes: Record<string, number>;
  is_official: boolean;
};

type AssessmentFormErrors = AxiosError & {
  response: {
    data: {
      non_field_errors: string[];
    };
  };
};

export const AssessmentGradingForm: FC<AssessmentGradingProps> = ({ page, nextURL, prevURL }) => {
  const { getQueryParam } = useQueryParamsManager();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const org = useAppSelector(selectCurrentOrg);
  const courseUserId = useAppSelector(selectCurrentCourseUserId);
  const pageOutcomes = useAppSelector((s) => selectNestedPageOutcomes(s, page.id));
  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));
  const assessment = useAppSelector((s) => selectAssessmentBySubmissionId(s, submissionId)) || null;
  const outcomeAssessments = useAppSelector(
    (s) => selectOutcomeAssessmentByAssessmentId(s, assessment?.id) || [],
  );
  const courseUserMap = useAppSelector((s) => s.course_users);
  const maxPoints = page?.max_points;
  const hasOutcomes = pageOutcomes.length > 0;
  const isAssessing = useIsAssessing();

  const selectedAccessId = submission
    ? isIndividualSubmission(submission)
      ? studentAccessIdFromUserId(courseUserMap[submission.course_user_id]?.user_id)
      : groupAccessIdFromGroupId(submission.page_group_id)
    : undefined;

  const { createAssessment, updateAssessment, deleteAssessment } = useAssessmentActions();

  // Need to determine if there is an official submission for this user/group
  const officialSubmission = useAppSelector((s) =>
    selectUserOfficialSubmissionByPageId(s, page.id, selectedAccessId),
  );

  const isOfficial = !!submission?.official_at;

  const blankFormValues = useMemo(
    () => ({
      points: NaN,
      comment: "",
      page_outcomes: Object.fromEntries(pageOutcomes.map((po) => [po.id, NaN])),
      is_official: false,
    }),
    [pageOutcomes],
  );

  const assessmentFormValues = useMemo(() => {
    return {
      points: assessment?.points,
      comment: assessment?.comment,
      page_outcomes: Object.fromEntries(
        outcomeAssessments.map((oa) => [oa.page_outcome_id, oa.score]),
      ),
      is_official: !!submission?.official_at,
    };
  }, [assessment, outcomeAssessments, submission?.official_at]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<AssessmentFormInputType>({
    defaultValues: assessment ? assessmentFormValues : blankFormValues,
  });

  const scores = Object.values(watch("page_outcomes"));
  const points = watch("points");
  const comment = watch("comment");

  const isManuallyScoring = points
    ? calculatePointsFromOutcomeScores(scores, maxPoints, org.rubric) !== points
    : false;

  const hasPoints = maxPoints !== 0;
  const isAnyOutcomeScored = scores !== undefined && scores.some((os) => !isNaN(os));

  // The points configuration is only shown _after_ the user has selected an outcome score for any outcome.
  // TODO: It'd be nice to change this design in the future so that the config is only hidden when not
  //  applicable, not when outcome scores aren't selected.
  const showPoints = hasPoints && (isManuallyScoring || isAnyOutcomeScored);

  const formIsBlank = comment === "" && scores.every(isNaN) && !points;
  const showDelete = assessment?.id && formIsBlank;
  const isFormDirty =
    showDelete ||
    (!formIsBlank && !assessment) ||
    (assessment && (comment !== assessment.comment || points !== assessment.points)) ||
    scores.some((score, index) => {
      const pageOutcomeId = pageOutcomes[index]?.id;
      const outcomeAssessment = outcomeAssessments.find(
        (oa) => oa.page_outcome_id === pageOutcomeId,
      );
      return outcomeAssessment ? score !== outcomeAssessment.score : !isNaN(score);
    });

  const showUnofficialWarnings = !!officialSubmission && !isOfficial && !showDelete && !formIsBlank;

  const setScore = useCallback(
    (pageOutcomeId: string, score: number | null) => {
      setValue(`page_outcomes.${pageOutcomeId}`, score);
    },
    [setValue],
  );

  const updatePoints = useCallback(
    (newPoints?: number) => {
      if (!isNaN(newPoints)) {
        setValue("points", newPoints);
      } else if (!isManuallyScoring) {
        const updated_scores = Object.values(watch("page_outcomes"));
        setValue("points", calculatePointsFromOutcomeScores(updated_scores, maxPoints, org.rubric));
      }
    },
    [setValue, isManuallyScoring, maxPoints, org.rubric, watch],
  );

  const setScoreAndUpdatePoints = (pageOutcomeId: string, score: number | null) => {
    setScore(pageOutcomeId, score);
    updatePoints();
  };

  /// ////////// AI related code ///////////////

  // Having the button toggled is equivalent to clicking the AI assessment button,
  // every time the form is empty.
  const isAssessWithAIModeEnabled = useAppSelector(selectIsAssessWithAIModeEnabled);
  const [isAiAssessment, setIsAiAssessment] = useState(false);
  const [isAiAssessmentPromiseResolved, setIsAiAssessmentPromiseResolved] = useState(false);
  const [hasResetAiAssessment, setHasResetAiAssessment] = useState(false);

  const onClickResetAiAssessment = () => {
    setIsAiAssessment(false);
    reset(assessment ? assessmentFormValues : blankFormValues);
    if (submission?.id) {
      storeApi.assessments.list({ submission_id: submission.id });
    }
    setHasResetAiAssessment(true);
  };

  const setFormToAiAssessment = useCallback(
    (aiAssessment: AIAssessmentResponseType) => {
      aiAssessment.outcomes.forEach((oa) => {
        setScore(oa.pageOutcomeId, oa.score);
      });

      const outcomesScores = aiAssessment.outcomes.map((oa) => oa.score);
      const newPoints = calculatePointsFromOutcomeScores(outcomesScores, maxPoints, org.rubric);
      updatePoints(newPoints);
      setValue("comment", aiAssessment.feedback);
      if (aiAssessment.errorMessage) {
        setError("root", {
          type: "manual",
          message: aiAssessment.errorMessage,
        });
      } else {
        clearErrors("root");
      }
    },
    [setScore, setValue, maxPoints, org.rubric, setError, clearErrors, updatePoints],
  );

  const onClickAiAssess = useCallback(
    async (userClicked = false) => {
      setIsAiAssessment(true);
      setHasResetAiAssessment(false);
      setIsAiAssessmentPromiseResolved(false);
      // TODO: Show an option to re-generate the AI assessment (in case of error or simply for a refresh)
      const aiAssessment = await getOrRunAiAssessment(submission, false);
      trackEvent("AI assessment generated", { ...GENERAL_EVENT_CATEGORY, userClicked });
      setFormToAiAssessment(aiAssessment);
      setIsAiAssessmentPromiseResolved(true);
    },
    [submission, setFormToAiAssessment],
  );

  const maybeAssessWithAI = useCallback(() => {
    if (
      isAssessWithAIModeEnabled &&
      submission &&
      !isFormDirty &&
      !assessment &&
      !hasResetAiAssessment &&
      !isAiAssessment
    ) {
      onClickAiAssess(false);
    }
  }, [
    isAssessWithAIModeEnabled,
    submission,
    assessment,
    isFormDirty,
    hasResetAiAssessment,
    isAiAssessment,
    onClickAiAssess,
  ]);

  useEffect(() => {
    if (submission?.id) {
      setIsAiAssessment(false);
      setIsAiAssessmentPromiseResolved(false);
      setHasResetAiAssessment(false);
      clearErrors("root");
    }
  }, [submission?.id, clearErrors]);

  useEffect(() => {
    maybeAssessWithAI();
    // Handle changes to these dependencies separately than those below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssessWithAIModeEnabled, isFormDirty, hasResetAiAssessment, isAiAssessment]);

  /// ////////// End of AI related code ///////////////

  useEffect(() => {
    if (submission?.id && !assessment) {
      reset(blankFormValues);
      maybeAssessWithAI();
      storeApi.assessments.list({ submission_id: submission.id });
    }
    // Handle changes to these dependencies separately than those above, which
    // ensures the form is always reset before the AI assessment content is loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission, assessment]);

  useEffect(() => {
    if (assessment?.id) {
      storeApi.outcome_assessments.list({ assessment_id: assessment.id });
    }

    const page_outcomes = Object.fromEntries(
      pageOutcomes.map((po) => {
        const outcomeAssessment = outcomeAssessments.find((oa) => oa.page_outcome_id === po.id);
        return [po.id, outcomeAssessment ? outcomeAssessment.score : NaN];
      }),
    );

    reset({ comment: assessment?.comment || "", points: assessment?.points, page_outcomes });
  }, [assessment, outcomeAssessments, pageOutcomes, setScore, reset]);

  useEffect(() => {
    dispatch(viewStateActions.setShouldShowModalOnExitAssessmentMode(!formIsBlank && isFormDirty));
    return () => {
      dispatch(viewStateActions.setShouldShowModalOnExitAssessmentMode(false));
    };
  }, [isFormDirty, dispatch, formIsBlank]);

  useEffect(() => {
    dispatch(viewStateActions.setShouldShowModalOnExitAssessmentMode(false));
  }, [dispatch, assessment?.id]);

  const onSubmit: SubmitHandler<AssessmentFormInputType> = async (params, e) => {
    if (!courseUserId) {
      toastErrorMessage(t("assessment_card.cannot_assess_without_enrollment"));
      return;
    }

    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
    const submitSource = submitter?.value;

    try {
      const assessmentId = assessment?.id;
      const filteredPageOutcomes = Object.entries(params.page_outcomes).filter(
        ([, score]) => !isNaN(score),
      );

      const filteredParams = {
        ...params,
        is_official: submitSource === "keepOfficial" ? params.is_official : true,
        page_outcomes: Object.fromEntries(filteredPageOutcomes),
      };

      if (isFormDirty) {
        if (assessmentId) {
          if (
            !filteredParams.comment &&
            !filteredParams.points &&
            Object.values(filteredParams.page_outcomes).length === 0
          ) {
            const confirmed = await showConfirmationModal({
              title: t("assessment.delete_modal.title"),
              labelText: t("assessment.delete_modal.description"),
              confirmButtonText: t("common.delete"),
              confirmButtonKind: "destructive",
              cancelButtonText: t("assessment.delete_modal.keep_assessment"),
            });
            if (confirmed) {
              await deleteAssessment(assessmentId);
            }
          } else {
            await updateAssessment(assessmentId, filteredParams, courseUserId, submission.id);
          }
        } else {
          await createAssessment(filteredParams, courseUserId, submission.id, page.course_id);
        }
      }

      if (submitSource === "next" && nextURL) {
        navigate(nextURL.pathname + nextURL.search);
      } else if (submitSource === "prev" && prevURL) {
        navigate(prevURL.pathname + prevURL.search);
      }
    } catch (error) {
      const axiosError = error as AssessmentFormErrors;
      // TODO: better error handling
      setError("root", {
        type: "manual",
        message: (axiosError.response.data.non_field_errors ||
          axiosError?.response?.data[0]) as string,
      });
    }
  };

  const handleFormFocus = () => {
    clearErrors("root");
  };

  const isDoingAiAssessment = isAiAssessment && isAssessing;
  const isLoading = isDoingAiAssessment && !isAiAssessmentPromiseResolved;
  const finishedLoading = isDoingAiAssessment && isAiAssessmentPromiseResolved;
  const isDisabled = isLoading;

  return (
    <form
      className="data-[loading]:animate-pulse"
      data-loading={isLoading || undefined}
      id="assessment-grading-form"
      onFocus={handleFormFocus}
      onSubmit={handleSubmit(onSubmit)}
    >
      {isAiAssessment && (
        <div className="body-xs mb-2 flex items-center gap-2 rounded-md bg-lavender-tint-95 px-2 py-1 text-black-tint-20">
          <DoubleSparkleIcon />
          {t("assessment_card.ai.ai_generated")}
        </div>
      )}

      {isLoading &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="fixed inset-0 flex items-center justify-center bg-white/50">
            <LoadingDotsIcon />
          </div>,
          document.body,
        )}

      <div
        className={clsx("animate-shine-base relative", {
          "animate-shine-active": finishedLoading,
        })}
      >
        {hasOutcomes && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              {pageOutcomes.map((pageOutcome) => (
                <OutcomeAssessment
                  disabled={isDisabled}
                  key={pageOutcome.id}
                  pageOutcome={pageOutcome}
                  register={register(`page_outcomes.${pageOutcome.id}`, {
                    valueAsNumber: true,
                    required: false,
                  })}
                  score={watch(`page_outcomes.${pageOutcome.id}`)}
                  setScore={setScoreAndUpdatePoints}
                />
              ))}
            </div>

            {showPoints && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <h3 className="mx-0 my-0 text-sm font-semibold text-black-tint-20">
                    {t("submission_assessment.total_points")}
                  </h3>

                  <PointsConfig
                    decimal={1}
                    disabled={isDisabled}
                    page={page}
                    saveTextKey="update"
                    studentPoints={watch("points")}
                    validatePoints={(currentPoints: number) =>
                      !isNaN(currentPoints) && currentPoints >= 0
                    }
                    onSave={(currentPoints) => {
                      updatePoints(currentPoints);
                    }}
                  />
                </div>

                <div className="flex w-full justify-end">
                  {isManuallyScoring && (
                    <Button
                      kind="transparentSquareLegacy"
                      size="xs"
                      onClick={() => {
                        updatePoints(
                          calculatePointsFromOutcomeScores(scores, maxPoints, org.rubric),
                        );
                      }}
                    >
                      {t("common.reset_points")}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-3 flex w-full flex-col gap-1">
          <h4 className="mx-0 my-0 text-sm font-semibold">
            {t("submission_assessment.instructor_comment")}
          </h4>

          <BaseTextArea
            {...register("comment", {
              disabled: isDisabled,
            })}
            aria-label="Comment"
            className="mb-1 text-black-tint-20"
            minRows={3}
            placeholder={t("submission_assessment.comment_placeholder")}
            size="s"
            onClick={() => {
              trackEvent("Assessment - Add comment", BUTTON_EVENT_CATEGORY);
            }}
          />
        </div>
      </div>

      {errors.root && <span className="body-s text-red">{errors.root.message}</span>}

      {showUnofficialWarnings && (
        <div className="body-s italic text-black-tint-20">
          {t("assessment_card.official_assessment_explanation")}
        </div>
      )}

      <div className="mt-2 flex flex-row justify-end gap-1">
        {isAiAssessment ? (
          <Button
            className={clsx(!isDisabled && "text-black-tint-20")}
            disabled={isDisabled}
            eventName="Assessment - Reset"
            kind="white"
            size="xs"
            onClick={onClickResetAiAssessment}
          >
            {t("assessment_card.ai.reset_assessment")}
          </Button>
        ) : (
          <Button
            className={clsx("flex gap-1", !isDisabled && "text-black-tint-20")}
            disabled={isDisabled}
            eventName="Assessment - Assess with AI"
            kind="white"
            size="xs"
            onClick={() => onClickAiAssess(true)}
          >
            {t("assessment_card.ai.assess_with_ai")}
            <DoubleSparkleIcon className={clsx(isDisabled && "text-black-tint-55")} />
          </Button>
        )}
      </div>

      {showUnofficialWarnings && (
        <div className="mt-2">
          <LinkButton
            className="body-s bold cursor-pointer text-blue"
            name="submitSource"
            type="submit"
            value="keepOfficial"
          >
            {t("assessment_card.save_but_do_not_change_the_official_submission")}
          </LinkButton>
        </div>
      )}
    </form>
  );
};
